import * as React from 'react';
import {DragDropContext, Droppable} from 'react-beautiful-dnd';
import type {DropResult} from 'react-beautiful-dnd';
import {connect} from 'react-redux';

import * as ramda from 'ramda';

import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';

import type {SalesAutomationStage as SalesAutomationStageFragment} from 'nutshell-graphql-types';

import {setActiveItem, setHoveredStageId} from '../../ui-actions';

import {StageAddButton} from './stage-add-button';
import {StageHeaderChevron} from './stage-header-chevron';

import styles from './sales-automation-board-headers.module.css';

const COLUMN_WIDTH = 310;
const COLUMN_GUTTER = 6;

type OwnProps = {
    stages: SalesAutomationStageFragment[];
    onStageDragStart: () => any;
    onStageDragEnd: (result?: {stageId: string; position: number}) => any;
};

type DispatchProps = {
    onEditStage: (stageId: string) => void;
    onStageMouseEnter: (stageId: string) => void;
    onStageMouseLeave: () => void;
};

type Props = OwnProps & DispatchProps;

type State = {
    proposedStageMove:
        | {
              stageId: string;
              position: number;
          }
        | null
        | undefined;
    suppressFurtherStageMoveConfirmation: boolean;
};

// @ts-expect-error
const mapDispatchToProps = function (dispatch): DispatchProps {
    return {
        onEditStage: (stageId) => {
            dispatch(setActiveItem({type: 'stages', parentId: stageId, entityId: stageId}));
        },
        onStageMouseEnter: (stageId: string) => {
            dispatch(setHoveredStageId(stageId));
        },
        onStageMouseLeave: () => {
            dispatch(setHoveredStageId(null));
        },
    };
};

// This must extend Component rather thenPure component while it remains the
// direct parent of Droppable. Otherwise we have issues with dropping things
// in positions outside the original viewport
export class StageHeadersComponent extends React.Component<Props, State> {
    constructor() {
        // @ts-expect-error
        super();
        this.state = {
            proposedStageMove: null,
            suppressFurtherStageMoveConfirmation: false,
        };
    }

    render() {
        const stages = this.state.proposedStageMove
            ? this.getReorderedStages(this.state.proposedStageMove)
            : this.props.stages;

        return (
            <DragDropContext
                onDragStart={this.props.onStageDragStart}
                onDragEnd={this.handleStageDragEnd}
            >
                <Droppable droppableId='stages' direction='horizontal'>
                    {(provided) => (
                        <div
                            style={{width: this.getHeadersContainerWidth()}}
                            ref={provided.innerRef}
                            className={styles['headers-container']}
                        >
                            {stages.map((stage, index) => {
                                return (
                                    <StageHeaderChevron
                                        key={stage.id}
                                        onClick={this.props.onEditStage}
                                        stage={stage}
                                        index={index}
                                        onMouseEnter={this.props.onStageMouseEnter}
                                        onMouseLeave={this.props.onStageMouseLeave}
                                        isFirstChild={index === 0}
                                        isLastChild={false}
                                    />
                                );
                            })}
                            {provided.placeholder}
                            <StageAddButton isEmptyBoard={this.props.stages.length === 0} />
                            {Boolean(this.state.proposedStageMove) &&
                            !this.state.suppressFurtherStageMoveConfirmation ? (
                                <ConfirmationDialog
                                    title='Are you sure you want to reorder these stages?'
                                    message='Reordering stages can cause confusion in the funnel report and other side effects with open leads.'
                                    onCancel={this.cancelStageMove}
                                    onConfirm={this.confirmStageMove}
                                />
                            ) : null}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        );
    }

    getReorderedStages = (proposedMove: {stageId: string; position: number}) => {
        const {stageId, position} = proposedMove;
        const movedStage = this.props.stages.find((stage) => stage.id === stageId);

        if (!movedStage) return this.props.stages;
        const reorderStages = ramda.compose(
            ramda.insert(position - 1, movedStage),
            ramda.without([movedStage])
        );

        return reorderStages(this.props.stages);
    };

    /**
     * Helper function to get an explicit width for the headers container.
     *
     * While setting an explicit width does not affect the visual styling
     * of the headers, it is required for react-beautiful-dnd's
     * dragging functionality to work on horizontally scrolling screens.
     *
     * Refs: https://github.com/atlassian/react-beautiful-dnd/issues/268
     *
     * @return {number}      Explicit width to set
     */
    getHeadersContainerWidth = () => {
        const totalStageWidth = COLUMN_WIDTH + COLUMN_GUTTER;

        return (
            totalStageWidth * this.props.stages.length -
            // Subtract 1 column gutter, last stage doesn't have gutter
            COLUMN_GUTTER +
            // Add 4 pixels for the first child's additional width
            4
        );
    };

    handleStageDragEnd = (result: DropResult) => {
        const stageId = result.draggableId;
        const movedStage = this.props.stages.find((stage) => stage.id === stageId);

        if (
            !result.destination ||
            !movedStage ||
            result.destination.index + 1 === movedStage.position
        ) {
            this.props.onStageDragEnd();
            this.endStageMove(this.state.suppressFurtherStageMoveConfirmation);

            return;
        }

        const proposedStageMove = {
            stageId,
            position: result.destination.index + 1,
        } as const;

        if (this.state.suppressFurtherStageMoveConfirmation) {
            this.props.onStageDragEnd(proposedStageMove);
            this.endStageMove(true);
        } else {
            this.setState({proposedStageMove});
        }
    };

    confirmStageMove = () => {
        if (this.state.proposedStageMove) {
            this.props.onStageDragEnd(this.state.proposedStageMove);
        }

        this.endStageMove(true);
    };

    cancelStageMove = () => {
        this.props.onStageDragEnd();
        this.endStageMove(false);
    };

    endStageMove = (suppressFurtherStageMoveConfirmation: boolean) => {
        this.setState({
            proposedStageMove: null,
            suppressFurtherStageMoveConfirmation,
        });
    };
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(null, mapDispatchToProps);
export const StageHeaders = connector(StageHeadersComponent);
